import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { formatDate } from '@/lib/utils';
import { showSuccess } from '@/lib/swal';
import { CheckCircle, Copy, Share2 } from 'lucide-react';
import { Event } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

export default function InterestConfirmationPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!unitId || !eventId) {
        setLoading(false);
        return;
      }

      try {
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (eventDoc.exists()) {
          const data = eventDoc.data();
          setEvent({
            id: eventDoc.id,
            unitId: unitId,
            name: data.name,
            ceremonyType: data.ceremonyType,
            date: data.date?.toDate() || new Date(),
            location: data.location,
            costPerPerson: data.costPerPerson,
            questionnaire: data.questionnaire || [],
            courses: data.courses || [],
            maxCapacity: data.maxCapacity,
            currentAttendees: data.currentAttendees || 0,
            waitlistEnabled: data.waitlistEnabled || false,
            waitlist: data.waitlist || [],
            rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
            paymentDeadline: data.paymentDeadline?.toDate() || (data.date?.toDate?.() || new Date()),
            eventStatus: data.eventStatus,
            dresscode: data.dresscode,
            customInstructions: data.customInstructions,
            isPublic: data.isPublic ?? false,
            requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
            createdBy: data.createdBy,
            createdAt: data.createdAt?.toDate() || new Date(),
          });
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [unitId, eventId]);

  const handleCopyInterestLink = async () => {
    if (!event) return;
    
    const interestLink = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp`;
    
    try {
      await navigator.clipboard.writeText(interestLink);
      showSuccess('Interest registration link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-3xl space-y-6">
          {/* Success Confirmation */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-green-900 mb-2">Interest Registered!</h2>
                  <p className="text-green-800">
                    You have successfully registered your interest for this event.
                  </p>
                  {event && (
                    <p className="text-green-700 font-medium mt-2">
                      {event.name}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's Next Card */}
          <Card>
            <CardHeader>
              <CardTitle>What's Next?</CardTitle>
              <CardDescription>
                Here's what will happen now
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold">Event Planning</h3>
                    <p className="text-sm text-muted-foreground">
                      The organizers are finalizing details such as menu, pricing, and arrangements.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold">Notification Email</h3>
                    <p className="text-sm text-muted-foreground">
                      You'll receive an email when the event is fully published with all details.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold">Complete Your RSVP</h3>
                    <p className="text-sm text-muted-foreground">
                      Select meal preferences, provide dietary requirements, and confirm attendance.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t space-y-3">
                <h4 className="font-semibold text-sm">Share with Others</h4>
                <p className="text-sm text-muted-foreground">
                  Know someone who might be interested? Share the registration link with them.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCopyInterestLink}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp`)}
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    View Event
                  </Button>
                </div>

                {currentUser ? (
                  <Button
                    className="w-full"
                    onClick={() => navigate('/dashboard')}
                  >
                    Return to Dashboard
                  </Button>
                ) : (
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="pt-6">
                      <div className="text-center space-y-3">
                        <div>
                          <h3 className="font-semibold text-blue-900">Create an Account</h3>
                          <p className="text-sm text-blue-800 mt-1">
                            Track your RSVPs, manage your profile, and get notified about future events.
                          </p>
                        </div>
                        <Button
                          className="w-full"
                          onClick={() => navigate('/signup', { state: { returnTo: `/unit/${unitId}/event/${eventId}/rsvp` } })}
                        >
                          Create Free Account
                        </Button>
                        <p className="text-xs text-blue-700">
                          Already have an account?{' '}
                          <button
                            onClick={() => navigate('/login', { state: { returnTo: `/unit/${unitId}/event/${eventId}/rsvp` } })}
                            className="underline hover:text-blue-900"
                          >
                            Log in
                          </button>
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Important Information */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <h4 className="font-semibold text-amber-900">Keep an Eye on Your Email</h4>
                <p className="text-sm text-amber-800">
                  Make sure to check your inbox regularly. We'll send you updates as the event details are confirmed.
                  The email will include the full event information and a link to complete your RSVP.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

